import { Link } from "react-router-dom";
import { Lock } from "lucide-react";
import type { LogoPosition } from "@juegossc/shared";
import { RuletaIcon, RaspaditaIcon, MemoriaIcon } from "../ui/GameIcons";
import { ModoPruebaBanner } from "../ui/Banner";

// LandingView — markup PURO de la pantalla principal del cliente, SIN hooks
// de datos. Es la UNICA fuente de verdad del layout: la renderizan tanto la
// pantalla real (routes/Landing.tsx, con datos del server) como el preview
// del admin (con los valores EN VIVO del form). Asi "ajusto en el admin y en
// la pantalla se ve TAL CUAL" — mismo JSX, mismas clases, mismas medidas.
//
// `preview`: cuando lo usa el admin. Cambia fixed->absolute (para no escapar
// del lienzo escalado), no navega (los botones son <div>, no <Link>) y no
// monta LockScreen. `guides`: dibuja rectangulos punteados de margen sobre
// logo/campaña/iconos para ver hasta donde llega cada elemento al ajustar.

// Subconjunto de global_settings que el layout del landing realmente lee.
// Tipado como subset para que el state real (mas campos) y el objeto armado
// del form (solo estos) sean ambos asignables.
export type LandingViewGlobal = {
  logo_url: string | null;
  logo_width_px: number;
  logo_position: LogoPosition;
  // Migration 044: aire arriba del logo (lo baja desde el borde superior).
  landing_logo_top_margin_px: number;
  campana_max_height_px: number;
  landing_campana_top_margin_px: number;
  landing_campana_to_iconos_gap_px: number;
  // Migration 045: posición vertical fina (0..100%) — reemplaza el enum
  // landing_iconos_vertical_align (top/center/bottom).
  landing_vertical_pos_pct: number;
  landing_iconos_horizontal_align: "left" | "center" | "right";
  landing_icon_size_px: number;
  icon_ruleta_url: string | null;
  icon_raspadita_url: string | null;
  icon_memoria_url: string | null;
};

export type LandingViewSettings = {
  modo_prueba: boolean;
  ruleta_habilitada: boolean;
  raspadita_habilitada: boolean;
  memoria_habilitada: boolean;
};

export type LandingViewCampana = {
  nombre_display: string;
  imagen_url: string;
} | null;

// Guia de margen: outline punteado que marca el rectangulo del elemento.
const GUIDE = "1px dashed rgba(255,255,255,0.55)";

export function LandingView({
  global,
  settings,
  campana,
  preview = false,
  guides = false,
  onLock,
  ruletaSlots,
}: {
  global: LandingViewGlobal;
  settings: LandingViewSettings;
  campana: LandingViewCampana;
  preview?: boolean;
  guides?: boolean;
  onLock?: () => void;
  // Cantidad de premios de la ruleta → el ícono por defecto de Ruleta dibuja
  // esa cantidad de gajos (rueda real). Si falta, RuletaIcon usa su default.
  ruletaSlots?: number;
}) {
  // Migration 045: posición vertical fina del bloque campaña+íconos via
  // espaciadores flex (mismo patrón que RuletaView). 0 = pegado arriba (bajo el
  // logo), 50 = centrado, 100 = abajo. El bloque (shrink-0) nunca se recorta.
  const topGrow = Math.max(0, Math.min(100, global.landing_vertical_pos_pct));
  const bottomGrow = 100 - topGrow;
  return (
    <div
      // Capa de contenido TRANSPARENTE que llena su lienzo (1080×1920). El
      // fondo lo pone el contenedor que la escala (FitScreen en la pantalla
      // real, VirtualScreen en el preview), así ambos comparten el mismo
      // lienzo y se ven idénticos a cualquier resolución.
      className="app-mode flex h-full w-full flex-col"
      style={{ position: "relative" }}
    >
      {settings.modo_prueba && <ModoPruebaBanner />}

      {/* Header: logo del shopping. Tamaño/posición de global_settings.
          pr-20 reserva el espacio del botón candado (fixed top-right).
          Migration 044: marginTop configurable = "aire arriba del logo" (lo baja
          desde el borde superior; el body flex-1 reabsorbe el espacio). */}
      <header
        className={`flex items-center pl-6 pr-20 py-6 ${
          global.logo_position === "left"
            ? "justify-start"
            : global.logo_position === "right"
            ? "justify-end"
            : "justify-center"
        }`}
        style={{ marginTop: `${global.landing_logo_top_margin_px}px` }}
      >
        {global.logo_url ? (
          <img
            src={global.logo_url}
            alt="Shopping China"
            style={{
              width: `${global.logo_width_px}px`,
              ...(guides ? { outline: GUIDE, outlineOffset: 2 } : null),
            }}
            className="h-auto max-w-full object-contain"
          />
        ) : (
          <div
            className="font-heading text-3xl font-bold opacity-50"
            style={guides ? { outline: GUIDE, outlineOffset: 2 } : undefined}
          >
            Shopping China
          </div>
        )}
      </header>

      {/* Body: imagen de campaña + botones de juegos. Migration 045: el bloque
          (campaña + íconos) se posiciona vertical con espaciadores flex segun el
          % (landing_vertical_pos_pct), igual que RuletaView -> control fino
          arriba<->abajo, sin recortarse. El gap campaña<->íconos vive en el
          bloque interno. */}
      <section className="flex flex-1 flex-col p-6">
        <div aria-hidden style={{ flex: `${topGrow} 1 0` }} />
        <div
          className="flex shrink-0 flex-col"
          style={{ gap: `${global.landing_campana_to_iconos_gap_px}px` }}
        >
        {campana?.imagen_url ? (
          <img
            src={campana.imagen_url}
            alt={campana.nombre_display}
            style={{
              // height (no max-height): el slider AGRANDA la imagen (igual que
              // el logo con width), no solo la achica. Antes con max-height la
              // imagen no pasaba de su tamaño natural → el slider "no hacía
              // efecto" arriba de ese punto. object-contain + max-w-full evitan
              // deformar/desbordar (si es muy ancha para la pantalla, se topa
              // al ancho). shrink-0 para que el flex no la encoja.
              height: `${global.campana_max_height_px}px`,
              marginTop: `${global.landing_campana_top_margin_px}px`,
              ...(guides ? { outline: GUIDE, outlineOffset: 2 } : null),
            }}
            className="mx-auto w-auto max-w-full shrink-0 object-contain drop-shadow-2xl"
          />
        ) : (
          <div
            className="mx-auto font-heading text-5xl font-bold opacity-30"
            style={{
              marginTop: `${global.landing_campana_top_margin_px}px`,
              ...(guides ? { outline: GUIDE, outlineOffset: 2 } : null),
            }}
          >
            {campana?.nombre_display ?? "Sin campaña activa"}
          </div>
        )}

        <div
          className={`flex flex-wrap items-center gap-6 ${
            global.landing_iconos_horizontal_align === "left"
              ? "justify-start"
              : global.landing_iconos_horizontal_align === "right"
              ? "justify-end"
              : "justify-center"
          }`}
        >
          {settings.ruleta_habilitada && (
            <GameButton
              to="/ruleta"
              sizePx={global.landing_icon_size_px}
              gradient="from-[#f7c948] to-[#ff9a3c]"
              textColor="text-[#1a1540]"
              iconUrl={global.icon_ruleta_url}
              FallbackIcon={RuletaIcon}
              label="Ruleta"
              preview={preview}
              guides={guides}
              iconSlots={ruletaSlots}
            />
          )}
          {settings.raspadita_habilitada && (
            <GameButton
              to="/raspadita"
              sizePx={global.landing_icon_size_px}
              gradient="from-[#a78bfa] to-[#7c3aed]"
              textColor="text-white"
              iconUrl={global.icon_raspadita_url}
              FallbackIcon={RaspaditaIcon}
              label="Raspadita"
              preview={preview}
              guides={guides}
            />
          )}
          {settings.memoria_habilitada && (
            <GameButton
              to="/memoria"
              sizePx={global.landing_icon_size_px}
              gradient="from-[#06b6d4] to-[#0e7490]"
              textColor="text-white"
              iconUrl={global.icon_memoria_url}
              FallbackIcon={MemoriaIcon}
              label="Memoria"
              preview={preview}
              guides={guides}
            />
          )}
        </div>
        </div>
        <div aria-hidden style={{ flex: `${bottomGrow} 1 0` }} />
      </section>

      {/* Botón candado, absolute al lienzo (top-right) para escalar con todo.
          En preview no hace nada. */}
      <button
        onClick={preview ? undefined : onLock}
        aria-label="Bloquear pantalla"
        tabIndex={preview ? -1 : 0}
        className="absolute top-3 right-3 z-50 rounded-full bg-black/40 p-3 text-white/50 backdrop-blur-sm hover:bg-black/60 hover:text-white/90"
        style={{ touchAction: "manipulation" }}
      >
        <Lock className="h-5 w-5" />
      </button>
    </div>
  );
}

// Botón cuadrado de juego. Mismo markup que la pantalla real; en preview es
// un <div> (no navega).
function GameButton({
  to,
  sizePx,
  gradient,
  textColor,
  iconUrl,
  FallbackIcon,
  label,
  preview,
  guides,
  iconSlots,
}: {
  to: string;
  sizePx: number;
  gradient: string;
  textColor: string;
  iconUrl: string | null;
  FallbackIcon: React.ComponentType<{ style?: React.CSSProperties; slots?: number }>;
  label: string;
  preview: boolean;
  guides: boolean;
  iconSlots?: number;
}) {
  const iconPx = Math.round(sizePx * 0.43);
  const fontPx = Math.round(sizePx * 0.11);
  const className = `flex flex-col items-center justify-center gap-3 rounded-3xl bg-gradient-to-br ${gradient} ${textColor} shadow-2xl transition hover:scale-105 active:scale-100`;
  const style: React.CSSProperties = {
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    ...(guides ? { outline: GUIDE, outlineOffset: 2 } : null),
  };
  const inner = (
    <>
      {iconUrl ? (
        <img
          src={iconUrl}
          alt=""
          style={{ width: `${iconPx}px`, height: `${iconPx}px` }}
          className="object-contain"
          draggable={false}
        />
      ) : (
        <FallbackIcon
          style={{ width: `${iconPx}px`, height: `${iconPx}px` }}
          slots={iconSlots}
        />
      )}
      <span style={{ fontSize: `${fontPx}px` }} className="font-display font-bold">
        {label}
      </span>
    </>
  );
  // En preview NO navegamos: un <div> con el mismo look (sin to).
  if (preview) {
    return (
      <div style={style} className={className}>
        {inner}
      </div>
    );
  }
  return (
    <Link to={to} style={style} className={className}>
      {inner}
    </Link>
  );
}
